import React from 'react'
import { motion } from 'framer-motion'
import { 
  Shield, 
  Target, 
  Users, 
  Globe, 
  Award,
  Heart,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  CheckCircle,
  Star,
  Building,
  Code,
  Lock,
  Eye,
  FileText,
  Smartphone,
  Wifi,
  AlertTriangle,
  BookOpen,
  Download,
  Clock,
  TrendingUp
} from 'lucide-react'

const About = () => {
  const features = [
    {
      icon: Target,
      title: 'Cyber Readiness Self-Check',
      description: 'Quick yes/no assessment to evaluate your agency\'s cybersecurity posture and identify areas for improvement.'
    },
    {
      icon: TrendingUp,
      title: 'Readiness Score & Action Tips',
      description: 'Get a clear risk score (Low, Medium, High) with actionable recommendations to enhance your security.'
    },
    {
      icon: FileText,
      title: 'Policy & Template Library',
      description: 'Access ready-to-use templates including Data Privacy Policies, Consent Forms, and compliance documents.'
    },
    {
      icon: BookOpen,
      title: 'Cyber Tips & Learning Corner',
      description: 'Educational content including videos, infographics, and guides on phishing, passwords, and safe practices.'
    },
    {
      icon: Clock,
      title: 'Task Reminders',
      description: 'Set schedules for important security tasks like backups, password changes, and staff training sessions.'
    },
    {
      icon: AlertTriangle,
      title: 'Incident Reporter',
      description: 'Quickly report suspicious activities and cybersecurity incidents to your IT team or DICT.'
    },
    {
      icon: Users,
      title: 'User Profiles',
      description: 'Customized experience for DPOs, IT staff, and administrators with role-specific content and features.'
    },
    {
      icon: Wifi,
      title: 'Offline Mode',
      description: 'Works offline and syncs when connected, perfect for remote areas or low-connectivity environments.'
    }
  ]

  const team = [
    {
      name: 'Department of Information and Communications Technology',
      role: 'Lead Development Agency',
      description: 'Spearheading digital transformation and cybersecurity initiatives across the Philippine government.',
      icon: Building
    },
    {
      name: 'National Privacy Commission',
      role: 'Privacy & Compliance Partner',
      description: 'Ensuring data privacy compliance and providing policy guidance for government agencies.',
      icon: Shield
    },
    {
      name: 'Cybersecurity Bureau',
      role: 'Security Expertise',
      description: 'Providing cybersecurity expertise, threat intelligence, and incident response capabilities.',
      icon: Lock
    },
    {
      name: 'Local Government Units',
      role: 'Implementation Partners',
      description: 'Pilot testing and feedback from various LGUs to ensure practical applicability.',
      icon: Globe
    }
  ]

  const stats = [
    {
      number: '1,200+',
      label: 'Government Agencies',
      description: 'Across the Philippines'
    },
    {
      number: '50,000+',
      label: 'Government Employees',
      description: 'Potential users'
    },
    {
      number: '95%',
      label: 'User Satisfaction',
      description: 'From pilot testing'
    },
    {
      number: '24/7',
      label: 'Support Available',
      description: 'Emergency response'
    }
  ]

  const timeline = [
    {
      year: '2023',
      title: 'Project Inception',
      description: 'Initial research and needs assessment conducted across government agencies.',
      icon: Target
    },
    {
      year: '2024 Q1',
      title: 'Development Phase',
      description: 'Core application development with focus on user experience and accessibility.',
      icon: Code
    },
    {
      year: '2024 Q2',
      title: 'Pilot Testing',
      description: 'Beta testing with selected LGUs and government agencies for feedback and improvements.',
      icon: Users
    },
    {
      year: '2024 Q3',
      title: 'National Rollout',
      description: 'Official launch and deployment across all Philippine government agencies.',
      icon: Globe
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-cyber-600 to-gov-600 rounded-full flex items-center justify-center">
              <Shield className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-display font-bold text-gov-800 mb-6">
            About CyberGov Check
          </h1>
          <p className="text-xl text-gov-600 max-w-4xl mx-auto leading-relaxed">
            A comprehensive cybersecurity assessment and improvement platform designed specifically for Philippine government offices. 
            Empowering non-technical users to enhance their cybersecurity posture through simple, actionable guidance.
          </p>
        </motion.div>

        {/* Mission & Vision */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16"
        >
          <motion.div variants={itemVariants} className="card">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-cyber-100 rounded-lg flex items-center justify-center">
                <Target className="w-6 h-6 text-cyber-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gov-800">Our Mission</h2>
            </div>
            <p className="text-gov-600 leading-relaxed">
              To strengthen the cybersecurity posture of Philippine government agencies by providing accessible, 
              practical tools and guidance that enable non-technical staff to implement effective security measures 
              and maintain compliance with national cybersecurity standards.
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="card">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gov-100 rounded-lg flex items-center justify-center">
                <Eye className="w-6 h-6 text-gov-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gov-800">Our Vision</h2>
            </div>
            <p className="text-gov-600 leading-relaxed">
              A digitally secure Philippine government where every agency, regardless of size or technical expertise, 
              maintains robust cybersecurity practices that protect citizen data and ensure the continuity of 
              essential government services.
            </p>
          </motion.div>
        </motion.div>

        {/* Key Features */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-16"
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gov-800 mb-4">Key Features</h2>
            <p className="text-lg text-gov-600 max-w-3xl mx-auto">
              Comprehensive tools designed to make cybersecurity accessible and manageable for government offices of all sizes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const FeatureIcon = feature.icon
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="card group hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="w-12 h-12 bg-cyber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-cyber-200 transition-colors duration-300">
                    <FeatureIcon className="w-6 h-6 text-cyber-600" />
                  </div>
                  <h3 className="font-semibold text-gov-800 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gov-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Statistics */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-16"
        >
          <motion.div variants={itemVariants} className="card bg-gradient-to-r from-cyber-600 to-gov-600 text-white">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">Impact & Reach</h2>
              <p className="text-cyber-100 max-w-2xl mx-auto">
                Supporting cybersecurity initiatives across the Philippine government ecosystem.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="text-center"
                >
                  <div className="text-3xl font-bold mb-2">{stat.number}</div>
                  <div className="text-lg font-medium mb-1">{stat.label}</div>
                  <div className="text-sm text-cyber-100">{stat.description}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Development Timeline */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-16"
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gov-800 mb-4">Development Timeline</h2>
            <p className="text-lg text-gov-600 max-w-3xl mx-auto">
              Our journey from concept to nationwide implementation.
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-cyber-200"></div>
            <div className="space-y-8">
              {timeline.map((item, index) => {
                const TimelineIcon = item.icon
                return (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                  >
                    <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                      <div className="card">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className={`w-10 h-10 bg-cyber-100 rounded-lg flex items-center justify-center ${
                            index % 2 !== 0 ? 'order-2' : ''
                          }`}>
                            <TimelineIcon className="w-5 h-5 text-cyber-600" />
                          </div>
                          <div className={index % 2 !== 0 ? 'order-1' : ''}>
                            <div className="text-sm font-medium text-cyber-600">{item.year}</div>
                            <h3 className="font-semibold text-gov-800">{item.title}</h3>
                          </div>
                        </div>
                        <p className="text-sm text-gov-600">{item.description}</p>
                      </div>
                    </div>
                    <div className="relative z-10">
                      <div className="w-4 h-4 bg-cyber-600 rounded-full border-4 border-white"></div>
                    </div>
                    <div className="w-1/2"></div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </motion.div>

        {/* Team & Partners */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-16"
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gov-800 mb-4">Our Team & Partners</h2>
            <p className="text-lg text-gov-600 max-w-3xl mx-auto">
              Collaboration between key government agencies to ensure comprehensive cybersecurity coverage.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {team.map((member, index) => {
              const MemberIcon = member.icon
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="card"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gov-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MemberIcon className="w-6 h-6 text-gov-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gov-800 mb-1">{member.name}</h3>
                      <div className="text-sm font-medium text-cyber-600 mb-2">{member.role}</div>
                      <p className="text-sm text-gov-600">{member.description}</p>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Technology & Security */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-16"
        >
          <motion.div variants={itemVariants} className="card">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gov-800 mb-4">Technology & Security</h2>
              <p className="text-lg text-gov-600 max-w-3xl mx-auto">
                Built with modern web technologies and following best practices for security and accessibility.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gov-800 mb-2">Mobile-First Design</h3>
                <p className="text-sm text-gov-600">
                  Responsive design that works seamlessly across all devices and screen sizes.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-semibold text-gov-800 mb-2">Security by Design</h3>
                <p className="text-sm text-gov-600">
                  End-to-end encryption, secure data handling, and compliance with government security standards.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Wifi className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gov-800 mb-2">Offline Capability</h3>
                <p className="text-sm text-gov-600">
                  Progressive Web App technology enables offline functionality and automatic syncing.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Contact & Support */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-16"
        >
          <motion.div variants={itemVariants} className="card">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gov-800 mb-4">Contact & Support</h2>
              <p className="text-lg text-gov-600">
                Get help, report issues, or provide feedback to improve CyberGov Check.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-cyber-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-cyber-600" />
                </div>
                <h3 className="font-semibold text-gov-800 mb-2">Email Support</h3>
                <p className="text-sm text-gov-600 mb-2">support@cybergov.gov.ph</p>
                <p className="text-xs text-gov-500">Response within 24 hours</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-cyber-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-6 h-6 text-cyber-600" />
                </div>
                <h3 className="font-semibold text-gov-800 mb-2">Hotline</h3>
                <p className="text-sm text-gov-600 mb-2">(02) 8888-4242</p>
                <p className="text-xs text-gov-500">24/7 Emergency Support</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-cyber-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-6 h-6 text-cyber-600" />
                </div>
                <h3 className="font-semibold text-gov-800 mb-2">Office</h3>
                <p className="text-sm text-gov-600 mb-2">DICT Building, Quezon City</p>
                <p className="text-xs text-gov-500">Mon-Fri 8:00 AM - 5:00 PM</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Footer CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="card bg-gradient-to-r from-cyber-600 to-gov-600 text-white">
            <h2 className="text-2xl font-bold mb-4">Ready to Strengthen Your Cybersecurity?</h2>
            <p className="text-cyber-100 mb-6 max-w-2xl mx-auto">
              Join thousands of government employees who are already using CyberGov Check to improve their cybersecurity posture.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-cyber-600 px-6 py-3 rounded-lg font-medium hover:bg-cyber-50 transition-colors duration-200">
                Start Assessment
              </button>
              <button className="border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white hover:text-cyber-600 transition-colors duration-200">
                Download Resources
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default About